import { Component, OnInit, inject, signal } from '@angular/core';

import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { Store } from '@ngrx/store';
import { catchError, of, switchMap } from 'rxjs';

import { AlvaMetricsDto } from '../../models/alva-metrics.model';
import { ThresholdParams } from '../../models/threshold-params.model';
import { InseminationAdviceService } from '../../services/insemination-advice.service';

const THRESHOLD_SETTINGS_KEY = 'insemination-advice-thresholds';
const SETTINGS_CONTEXT_NAME = 'DairyStartMonitor';
const DEFAULT_THRESHOLDS: ThresholdParams = {
  minAgeWithoutMeasurement: 15,
  minAgeWithMeasurement: 12,
  minimumHeightWithMeasurement: 135,
};

@Component({
  selector: 'alva-tab',
  templateUrl: './alva-tab.component.html',
  styleUrls: ['./alva-tab.component.scss'],
  imports: [MatProgressSpinner, TranslatePipe],
})
export class AlvaTabComponent implements OnInit {
  private readonly service = inject(InseminationAdviceService);
  private readonly settingsService = inject(SettingsService);
  private readonly store = inject(Store);
  private readonly translatePipe = inject(TranslatePipe);

  public loading = signal(true);
  public error = signal(false);
  public metrics = signal<AlvaMetricsDto | null>(null);

  ngOnInit(): void {
    this.fetchMetrics();
  }

  private fetchMetrics() {
    this.loading.set(true);
    this.error.set(false);
    this.settingsService
      .getSingle<ThresholdParams>(
        {
          contextName: SETTINGS_CONTEXT_NAME,
          key: THRESHOLD_SETTINGS_KEY,
          storage: SettingsStorage.Global,
          basedOnCurrentLocation: true,
        },
        DEFAULT_THRESHOLDS,
      )
      .pipe(
        catchError(() => {
          this.store.dispatch(
            NotificationActions.warningSnackbar({
              message: this.translatePipe.transform(
                'insemination-advice.error.settings-load-failed',
              ),
            }),
          );
          return of(DEFAULT_THRESHOLDS);
        }),
        switchMap((thresholds) =>
          this.service.getAlvaMetrics(thresholds.minimumHeightWithMeasurement).pipe(
            catchError(() => {
              this.error.set(true);
              this.metrics.set(null);
              return of(null);
            }),
          ),
        ),
      )
      .subscribe((m) => {
        this.metrics.set(m);
        this.loading.set(false);
      });
  }
}
